import { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Paper,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Stack,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { EventContext } from "../../contexts/EventContext";
import request from "../../utilities/requests";
import { toast } from "../../utilities/SnackBarUtils";

const STATUS_COLOR = {
  pending: "warning",
  approved: "success",
  paid: "success",
  flagged: "error",
};

// ── Review dialog ──────────────────────────────────────────────────────────

function ReviewDialog({ entry, open, onClose, onDone }) {
  const [status, setStatus] = useState("approved");
  const [adminNotes, setAdminNotes] = useState("");
  const [paidAt, setPaidAt] = useState(dayjs());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && entry) {
      setStatus(entry.status === "pending" ? "approved" : entry.status);
      setAdminNotes(entry.adminNotes || "");
      setPaidAt(entry.paidAt ? dayjs(entry.paidAt) : dayjs());
    }
  }, [open, entry]);

  const handleSave = async () => {
    setSaving(true);
    const body = { status, adminNotes };
    if (status === "paid") body.paidAt = paidAt.format("YYYY-MM-DD");
    const result = await request(`worklog/${entry._id}/status`, "post", body);
    setSaving(false);
    if (result?.success) {
      toast.success("Updated");
      onDone();
    }
  };

  if (!entry) return null;

  const deliverableTypeName =
    entry.deliverableType && typeof entry.deliverableType === "object"
      ? entry.deliverableType.name
      : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Review Submission</DialogTitle>
      <DialogContent>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          {entry.workType === "time" ? (
            <>
              <Typography variant="subtitle2">
                {entry.start
                  ? dayjs(entry.start).format("MMM D, YYYY h:mm A")
                  : "—"}
                {" – "}
                {entry.end ? dayjs(entry.end).format("h:mm A") : "—"}
                {entry.hoursWorked != null ? ` · ${entry.hoursWorked} hrs` : ""}
              </Typography>
              {entry.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {entry.description}
                </Typography>
              )}
            </>
          ) : (
            <>
              {deliverableTypeName && (
                <Typography variant="subtitle2">
                  {deliverableTypeName}
                </Typography>
              )}
              {entry.deliverableDescription && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {entry.deliverableDescription}
                </Typography>
              )}
            </>
          )}
        </Paper>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Decision</InputLabel>
          <Select
            value={status}
            label="Decision"
            onChange={(e) => setStatus(e.target.value)}
          >
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="flagged">Flagged</MenuItem>
            <MenuItem value="pending">Reset to Pending</MenuItem>
          </Select>
        </FormControl>
        {status === "paid" && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Payment Date"
              value={paidAt}
              onChange={(val) => setPaidAt(val)}
              slotProps={{ textField: { fullWidth: true, size: "small", sx: { mb: 2 } } }}
            />
          </LocalizationProvider>
        )}
        <TextField
          label="Admin Notes (optional)"
          multiline
          rows={3}
          fullWidth
          size="small"
          value={adminNotes}
          onChange={(e) => setAdminNotes(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={
            saving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Summary accordion ──────────────────────────────────────────────────────

function ContractorAccordion({ contractor }) {
  const name =
    [contractor.firstName, contractor.lastName].filter(Boolean).join(" ") ||
    contractor.email ||
    contractor._id;

  return (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            width: "100%",
            pr: 1,
          }}
        >
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>
              {name}
            </Typography>
            {contractor.email && name !== contractor.email && (
              <Typography variant="caption" color="text.secondary">
                {contractor.email}
              </Typography>
            )}
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ flexShrink: 0 }}
          >
            {contractor.totalHours != null && (
              <Typography variant="caption" color="text.secondary">
                {contractor.totalHours} hrs
              </Typography>
            )}
            {contractor.totalDeliverables != null && (
              <Typography variant="caption" color="text.secondary">
                {contractor.totalDeliverables} deliverables
              </Typography>
            )}
            {contractor.approvedCount > 0 && (
              <Chip
                label={`${contractor.approvedCount} approved`}
                color="success"
                size="small"
              />
            )}
            {contractor.flaggedCount > 0 && (
              <Chip
                label={`${contractor.flaggedCount} flagged`}
                color="error"
                size="small"
              />
            )}
            {contractor.pendingCount > 0 && (
              <Chip
                label={`${contractor.pendingCount} pending`}
                color="warning"
                size="small"
              />
            )}
          </Stack>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Divider sx={{ mb: 1.5 }} />
        {(contractor.entries || []).map((e) => (
          <Box key={e._id} sx={{ mb: 1.5 }}>
            {e.workType === "time" ? (
              <Typography variant="body2">
                <strong>
                  {e.start ? dayjs(e.start).format("MMM D, h:mm A") : "—"}
                  {" – "}
                  {e.end ? dayjs(e.end).format("h:mm A") : "—"}
                </strong>
                {e.hoursWorked != null ? ` · ${e.hoursWorked} hrs` : ""}
                {e.description ? ` — ${e.description}` : ""}
              </Typography>
            ) : (
              <Typography variant="body2">
                <strong>
                  {typeof e.deliverableType === "object"
                    ? e.deliverableType?.name
                    : "Deliverable"}
                </strong>
                {e.deliverableDescription
                  ? `: ${e.deliverableDescription}`
                  : ""}
              </Typography>
            )}
            <Chip
              label={e.status}
              color={STATUS_COLOR[e.status]}
              size="small"
              sx={{ mt: 0.5 }}
            />
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
}

// ── Deliverable types tab ──────────────────────────────────────────────────

function DeliverableTypesTab() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    setLoading(true);
    const result = await request("deliverabletype?includeInactive=true");
    setLoading(false);
    if (result?.success) setTypes(result.types || []);
  };

  const startEdit = (type) => {
    setEditingId(type._id);
    setEditName(type.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
  };

  const saveEdit = async (id) => {
    if (!editName.trim()) return;
    setSavingId(id);
    const result = await request(`deliverabletype/${id}`, "post", {
      name: editName.trim(),
    });
    setSavingId(null);
    if (result?.success) {
      toast.success("Updated");
      cancelEdit();
      fetchTypes();
    }
  };

  const toggleActive = async (type) => {
    setSavingId(type._id);
    const result = await request(`deliverabletype/${type._id}`, "post", {
      active: !type.active,
    });
    setSavingId(null);
    if (result?.success) {
      toast.success(type.active ? "Deactivated" : "Activated");
      fetchTypes();
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    const result = await request("deliverabletype", "post", {
      name: newName.trim(),
    });
    setCreating(false);
    if (result?.success) {
      toast.success("Type created");
      setNewName("");
      setCreateOpen(false);
      fetchTypes();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const active = types.filter((t) => t.active);
  const inactive = types.filter((t) => !t.active);

  const renderRow = (type) => {
    const isEditing = editingId === type._id;
    const isSaving = savingId === type._id;

    return (
      <Box
        key={type._id}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          py: 1.5,
          px: 2,
          opacity: type.active ? 1 : 0.5,
          borderBottom: 1,
          borderColor: "divider",
          "&:last-child": { borderBottom: 0 },
        }}
      >
        {isEditing ? (
          <>
            <TextField
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              size="small"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") saveEdit(type._id);
                if (e.key === "Escape") cancelEdit();
              }}
              sx={{ flexGrow: 1 }}
            />
            <IconButton
              size="small"
              color="primary"
              onClick={() => saveEdit(type._id)}
              disabled={isSaving}
            >
              {isSaving ? (
                <CircularProgress size={16} />
              ) : (
                <CheckIcon fontSize="small" />
              )}
            </IconButton>
            <IconButton size="small" onClick={cancelEdit} disabled={isSaving}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        ) : (
          <>
            <Typography variant="body2" sx={{ flexGrow: 1 }}>
              {type.name}
            </Typography>
            {!type.active && (
              <Chip
                label="Inactive"
                size="small"
                variant="outlined"
                color="default"
              />
            )}
            <IconButton
              size="small"
              onClick={() => startEdit(type)}
              aria-label="edit"
              disabled={isSaving}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <Button
              size="small"
              variant="outlined"
              color={type.active ? "error" : "success"}
              onClick={() => toggleActive(type)}
              disabled={isSaving}
              sx={{ minWidth: 90, flexShrink: 0 }}
            >
              {isSaving ? (
                <CircularProgress size={14} />
              ) : type.active ? (
                "Deactivate"
              ) : (
                "Activate"
              )}
            </Button>
          </>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          New Type
        </Button>
      </Box>

      {types.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          No deliverable types yet.
        </Typography>
      ) : (
        <Paper variant="outlined">
          {active.map(renderRow)}
          {inactive.length > 0 && active.length > 0 && <Divider />}
          {inactive.map(renderRow)}
        </Paper>
      )}

      {/* Create dialog */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>New Deliverable Type</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !newName.trim()}
            startIcon={
              creating ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function WorkLogAdmin() {
  const { userData } = useContext(EventContext);
  const isSuperAdmin = userData?.role === "superadmin";
  const [tab, setTab] = useState(0);

  // Submissions tab
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterWeekOf, setFilterWeekOf] = useState(null);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [reviewEntry, setReviewEntry] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);

  // Summary tab
  const [summaryWeekOf, setSummaryWeekOf] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEntries = async () => {
    setLoading(true);
    const params = [];
    if (filterWeekOf)
      params.push(`weekOf=${dayjs(filterWeekOf).format("YYYY-MM-DD")}`);
    if (filterType) params.push(`workType=${filterType}`);
    if (filterStatus) params.push(`status=${filterStatus}`);
    const query = "worklog/all" + (params.length ? "?" + params.join("&") : "");
    const result = await request(query);
    setLoading(false);
    if (result?.success) setEntries(result.entries || []);
  };

  const fetchSummary = async () => {
    if (!summaryWeekOf) {
      toast.error("Please select a week");
      return;
    }
    setSummaryLoading(true);
    const weekOf = dayjs(summaryWeekOf).format("YYYY-MM-DD");
    const result = await request(`worklog/summary?weekOf=${weekOf}`);
    setSummaryLoading(false);
    if (result?.success) setSummaryData(result);
  };

  const openReview = (entry) => {
    setReviewEntry(entry);
    setReviewOpen(true);
  };

  const handleReviewDone = () => {
    setReviewOpen(false);
    setReviewEntry(null);
    fetchEntries();
  };

  const getUserLabel = (entry) =>
    [entry.user?.firstName, entry.user?.lastName].filter(Boolean).join(" ") ||
    entry.user?.email ||
    entry.userId ||
    "—";

  const getEntryDetail = (entry) => {
    if (entry.workType === "time") {
      const time = entry.start
        ? `${dayjs(entry.start).format("MMM D, h:mm A")} – ${dayjs(entry.end).format("h:mm A")}`
        : "—";
      return entry.hoursWorked != null
        ? `${time} · ${entry.hoursWorked} hrs`
        : time;
    }
    const typeName =
      entry.deliverableType && typeof entry.deliverableType === "object"
        ? entry.deliverableType.name
        : "Deliverable";
    return entry.date ? `${typeName} · ${dayjs(entry.date).format("MMM D, YYYY")}` : typeName;
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Work Log Management
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Submissions" />
        <Tab label="Weekly Summary" />
        {isSuperAdmin && <Tab label="Deliverable Types" />}
      </Tabs>

      {/* ── Submissions Tab ── */}
      {tab === 0 && (
        <>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
              <Grid item xs={12} sm={3}>
                <DatePicker
                  label="Week Of"
                  value={filterWeekOf}
                  onChange={setFilterWeekOf}
                  slotProps={{ textField: { fullWidth: true, size: "small" } }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={filterType}
                    label="Type"
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="time">Time</MenuItem>
                    <MenuItem value="fixed">Fixed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    label="Status"
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="flagged">Flagged</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <Button variant="outlined" onClick={fetchEntries}>
                  Search
                </Button>
              </Grid>
            </Grid>
          </LocalizationProvider>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Contractor</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ py: 2 }}
                        >
                          No entries found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    entries.map((entry) => (
                      <TableRow key={entry._id} hover>
                        <TableCell>
                          <Typography variant="caption">
                            {getUserLabel(entry)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={entry.workType === "time" ? "Time" : "Fixed"}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {getEntryDetail(entry)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={entry.status}
                            color={STATUS_COLOR[entry.status] || "default"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            onClick={() => openReview(entry)}
                          >
                            Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* ── Weekly Summary Tab ── */}
      {tab === 1 && (
        <>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
              <Grid item xs={12} sm={4}>
                <DatePicker
                  label="Week Of"
                  value={summaryWeekOf}
                  onChange={setSummaryWeekOf}
                  slotProps={{ textField: { fullWidth: true, size: "small" } }}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  onClick={fetchSummary}
                  disabled={summaryLoading}
                  startIcon={
                    summaryLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                >
                  Load
                </Button>
              </Grid>
            </Grid>
          </LocalizationProvider>

          {summaryData && (
            <>
              {summaryData.weekStart && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {dayjs(summaryData.weekStart).format("MMM D")} –{" "}
                  {dayjs(summaryData.weekEnd).format("MMM D, YYYY")}
                </Typography>
              )}
              {!summaryData.contractors?.length ? (
                <Typography color="text.secondary" variant="body2">
                  No submissions for this week.
                </Typography>
              ) : (
                summaryData.contractors.map((contractor) => (
                  <ContractorAccordion
                    key={contractor._id}
                    contractor={contractor}
                  />
                ))
              )}
            </>
          )}
        </>
      )}

      {/* ── Deliverable Types Tab (superadmin only) ── */}
      {tab === 2 && isSuperAdmin && <DeliverableTypesTab />}

      <ReviewDialog
        entry={reviewEntry}
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        onDone={handleReviewDone}
      />
    </Box>
  );
}
